/*
 * == BSD2 LICENSE ==
 * Copyright (c) 2020, Tidepool Project
 *
 * This program is free software; you can redistribute it and/or modify it under
 * the terms of the associated License, which is identical to the BSD 2-Clause
 * License as published by the Open Source Initiative at opensource.org.
 *
 * This program is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS
 * FOR A PARTICULAR PURPOSE. See the License for more details.
 *
 * You should have received a copy of the License along with this program; if
 * not, you can obtain one from Tidepool Project at tidepool.org.
 * == BSD2 LICENSE ==
 */

// Constants for FTDI device
const C_CLK = 48_000_000
const DEFAULT_BUFFER_SIZE = 64
const FTDI_HEADER_SIZE = 2 // FTDI devices prepend 2 status bytes to each packet
const MAX_RETRY_ATTEMPTS = 3
const RETRY_DELAY_MS = 100

// FTDI USB constants
const FTDI_VENDOR_ID = 0x0403
const FTDI_SET_BAUD_RATE = 0x03

// FTDI device filters for requestDevice
export const FTDI_DEVICE_FILTERS = [{ vendorId: FTDI_VENDOR_ID }]

/**
 * Finds the in and out endpoint numbers for the first interface of a USBDevice.
 * Throws if endpoints cannot be determined.
 *
 * @param device - The USB device to inspect.
 * @returns Object containing inEndpoint and outEndpoint numbers
 * @throws Error if interface or endpoints cannot be determined
 */
function getFtdiEndpoints(device: USBDevice): {
	inEndpoint: number
	outEndpoint: number
} {
	const deviceInterface = device.configuration?.interfaces[0]
	if (!deviceInterface) throw new Error("No interface found on device")
	const inEndpoint = deviceInterface.alternate.endpoints.find(
		(endpoint) => endpoint.direction === "in",
	)?.endpointNumber
	const outEndpoint = deviceInterface.alternate.endpoints.find(
		(endpoint) => endpoint.direction === "out",
	)?.endpointNumber
	if (typeof inEndpoint !== "number" || typeof outEndpoint !== "number") {
		throw new Error("Could not determine in/out endpoint numbers")
	}
	return { inEndpoint, outEndpoint }
}

/**
 * Returns a promise that resolves after the specified number of milliseconds.
 * Utility for async timeouts and retry logic.
 *
 * @param ms - Milliseconds to delay
 * @returns Promise that resolves after the delay
 */
function delay(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Convert baud rate to FTDI clock bits
 * @param baud Desired baud rate
 * @param clk Clock frequency
 * @param clkDiv Clock divisor
 * @returns Array with calculated baud rate and encoded divisor
 */
interface FTDIToClkbitsParams {
	baud: number
	clk: number
	clkDiv: number
}

interface FTDIToClkbitsResult {
	bestBaud: number
	encodedDivisor: number
}

function FTDIToClkbits({
	baud,
	clk,
	clkDiv,
}: FTDIToClkbitsParams): FTDIToClkbitsResult {
	const fracCode = [0, 3, 2, 4, 1, 5, 6, 7]
	let bestBaud = 0
	let divisor: number
	let bestDivisor: number
	let encodedDivisor: number

	if (baud >= clk / clkDiv) {
		encodedDivisor = 0
		bestBaud = clk / clkDiv
	} else if (baud >= clk / (clkDiv + clkDiv / 2)) {
		encodedDivisor = 1
		bestBaud = clk / (clkDiv + clkDiv / 2)
	} else if (baud >= clk / (2 * clkDiv)) {
		encodedDivisor = 2
		bestBaud = clk / (2 * clkDiv)
	} else {
		divisor = (clk * 16) / clkDiv / baud
		if (divisor & 1) {
			bestDivisor = divisor / 2 + 1
		} else {
			bestDivisor = divisor / 2
		}

		if (bestDivisor > 0x20000) {
			bestDivisor = 0x1ffff
		}

		bestBaud = (clk * 16) / clkDiv / bestDivisor

		if (bestBaud & 1) {
			bestBaud = bestBaud / 2 + 1
		} else {
			bestBaud = bestBaud / 2
		}

		encodedDivisor = (bestDivisor >> 3) | (fracCode[bestDivisor & 0x7] << 14)
	}

	return {
		bestBaud,
		encodedDivisor,
	}
}

/**
 * Convert baud rate to FTDI control transfer parameters
 * @param baud Desired baud rate
 * @returns Array with calculated baud rate, value and index for control transfer
 * @throws Error if baud rate is invalid
 */
interface FTDIConvertBaudrateResult {
	bestBaud: number
	value: number
	index: number
}

function convertFTDIBaudrate(baud: number): FTDIConvertBaudrateResult {
	if (baud <= 0) {
		throw new Error("Baud rate must be > 0")
	}

	const { bestBaud, encodedDivisor } = FTDIToClkbits({
		baud,
		clk: C_CLK,
		clkDiv: 16,
	})

	const value = encodedDivisor & 0xffff
	const index = encodedDivisor >> 16

	return {
		bestBaud,
		value,
		index,
	}
}

/**
 * FTDI device connection options
 */
export interface FTDIOptions {
	baudRate: number
	bufferSize?: number
}

/**
 * FTDI device read result
 */
export interface FTDIReadResult {
	bytesRead: number
	data: Uint8Array
}

/**
 * FTDI device class implementing WebUSB best practices
 */
export class FTDI extends EventTarget {
	private readonly device: USBDevice
	private isClosing: boolean = false
	private isConnected: boolean = false
	private readonly options: Required<FTDIOptions>
	private readInProgress: boolean = false
	private retryCount: number = 0

	private inEndpoint: number = 1 // Default fallback, will be set in initialize
	private outEndpoint: number = 2 // Default fallback, will be set in initialize

	/**
	 * Create a new FTDI device instance
	 * @param device WebUSB device object
	 * @param options Configuration options
	 */
	constructor(device: USBDevice, options: FTDIOptions) {
		super()
		this.device = device
		this.options = {
			baudRate: options.baudRate,
			bufferSize: options.bufferSize || DEFAULT_BUFFER_SIZE,
		}

		// Setup event listeners for device disconnection
		navigator.usb.addEventListener(
			"disconnect",
			this.handleDisconnect.bind(this),
		)
	}

	/**
	 * Handle device disconnection events
	 * @param event USB disconnect event
	 */
	private handleDisconnect(event: USBConnectionEvent): void {
		if (event.device === this.device) {
			console.debug("Device disconnected")
			this.isConnected = false
			this.dispatchEvent(new Event("disconnect"))
		}
	}

	/**
	 * Check if device is connected
	 * @returns True if device is connected and ready
	 */
	public get connected(): boolean {
		return this.isConnected && this.device.opened
	}

	/**
	 * Get the connected device
	 * @returns The WebUSB device object
	 */
	public get usbDevice(): USBDevice {
		return this.device
	}

	/**
	 * Initialize the FTDI device
	 * @returns Promise that resolves when the device is ready
	 * @throws Error if device initialization fails
	 */
	public async initialize(): Promise<void> {
		if (this.isConnected) {
			console.debug("Device already initialized")
			return
		}

		try {
			if (!this.device) {
				throw new Error("No device provided")
			}

			// Reset retry counter
			this.retryCount = 0

			// Open the device
			await this.device.open()
			console.debug("Device opened:", this.device.opened)

			// Configure the device
			if (this.device.configuration === null) {
				await this.device.selectConfiguration(1)
			}

			await this.device.claimInterface(0)
			await this.device.selectAlternateInterface(0, 0)

			// Dynamically determine endpoints
			const { inEndpoint, outEndpoint } = getFtdiEndpoints(this.device)
			this.inEndpoint = inEndpoint
			this.outEndpoint = outEndpoint

			// Set baud rate
			const { bestBaud, value, index } = convertFTDIBaudrate(
				this.options.baudRate,
			)
			console.debug("Setting baud rate to", bestBaud)
			await this.device.controlTransferOut({
				requestType: "vendor",
				recipient: "device",
				request: FTDI_SET_BAUD_RATE,
				value,
				index,
			})

			// Flush any pending data
			await this.flushBuffers()

			// Start reading loop
			this.isConnected = true
			this.startReadLoop()

			// Notify that device is ready
			this.dispatchEvent(new Event("ready"))
		} catch (error) {
			console.error("Error during FTDI initialization:", error)

			// Try to clean up if initialization failed
			try {
				await this.closeAsync()
			} catch (cleanupError) {
				console.error("Error during cleanup:", cleanupError)
			}

			this.isConnected = false
			this.dispatchEvent(new CustomEvent("error", { detail: error }))
			throw error
		}
	}

	/**
	 * Flush device buffers
	 * @returns Promise that resolves when buffers are flushed
	 */
	private async flushBuffers(): Promise<void> {
		try {
			// Read any pending data to clear the buffers
			await this.device.transferIn(this.inEndpoint, this.options.bufferSize)
		} catch (error) {
			// Ignore errors during flush
			console.debug("Flush buffer warning (can be ignored):", error)
		}
	}

	/**
	 * Start the continuous read loop
	 */
	private startReadLoop(): void {
		if (this.readInProgress) {
			throw new Error("Read loop already in progress")
		}
		if (!this.isConnected) {
			throw new Error("Cannot start read loop: device is not connected")
		}
		this.readInProgress = true
		this.readLoop().catch((error) => {
			console.error("Read loop error:", error)
			this.readInProgress = false
		})
	}

	/**
	 * Continuous read loop to process incoming data
	 */
	private async readLoop(): Promise<void> {
		if (!this.isConnected || this.isClosing) {
			this.readInProgress = false
			return
		}

		try {
			const result = await this.device.transferIn(
				this.inEndpoint,
				this.options.bufferSize,
			)
			this.retryCount = 0
			this.processReadData(result)
			this.scheduleNextRead()
		} catch (error) {
			await this.handleReadError(error)
		}
	}

	/**
	 * Extracts and dispatches FTDI payload data from a USB transfer result.
	 *
	 * @param result - USB transfer result containing raw data from the device.
	 */
	private processReadData(result: USBInTransferResult): void {
		if (
			!result?.data?.byteLength ||
			result.data.byteLength <= FTDI_HEADER_SIZE
		) {
			return
		}
		const dataLength = result.data.byteLength - FTDI_HEADER_SIZE
		const data = new Uint8Array(
			result.data.buffer,
			result.data.byteOffset + FTDI_HEADER_SIZE,
			dataLength,
		)
		this.dispatchEvent(new CustomEvent("data", { detail: data }))
	}

	/**
	 * Continues the read loop if the device is still connected and not closing.
	 * Otherwise, marks the read loop as no longer in progress.
	 */
	private scheduleNextRead(): void {
		if (this.isConnected && !this.isClosing) {
			this.readLoop()
		} else {
			this.readInProgress = false
		}
	}

	/**
	 * Handles errors from the read loop, including retry logic and disconnect detection.
	 * Retries up to MAX_RETRY_ATTEMPTS on recoverable errors, or dispatches disconnect/error events.
	 *
	 * @param error - The error thrown during a read attempt.
	 */
	private async handleReadError(error: unknown): Promise<void> {
		if (error instanceof DOMException && error.name === "NotFoundError") {
			console.debug("Device disconnected during read")
			this.isConnected = false
			this.readInProgress = false
			this.dispatchEvent(new Event("disconnect"))
			return
		}
		if (this.retryCount < MAX_RETRY_ATTEMPTS) {
			this.retryCount++
			console.debug(
				`Read error, retrying (${this.retryCount}/${MAX_RETRY_ATTEMPTS}):`,
				error,
			)
			await delay(RETRY_DELAY_MS)
			this.scheduleNextRead()
		} else {
			console.error("Max read retries exceeded:", error)
			this.readInProgress = false
			this.dispatchEvent(new CustomEvent("error", { detail: error }))
		}
	}

	/**
	 * Read data from the device
	 * @returns Promise with read result containing bytesRead and data
	 */
	public async read(): Promise<FTDIReadResult> {
		if (!this.isConnected) {
			throw new Error("Device not connected")
		}
		if (this.readInProgress) {
			throw new Error("Read already in progress")
		}
		this.readInProgress = true
		try {
			const result = await this.device.transferIn(
				this.inEndpoint,
				this.options.bufferSize,
			)

			if (!result?.data) {
				return { bytesRead: 0, data: new Uint8Array() }
			}

			// Handle FTDI status bytes (first 2 bytes)
			if (result.data.byteLength <= FTDI_HEADER_SIZE) {
				return this.read() // No actual data, try again
			}

			const dataLength = result.data.byteLength - FTDI_HEADER_SIZE
			const data = new Uint8Array(
				result.data.buffer,
				result.data.byteOffset + FTDI_HEADER_SIZE,
				dataLength,
			)

			return { bytesRead: dataLength, data }
		} catch (error) {
			if (error instanceof DOMException && error.name === "NotFoundError") {
				this.isConnected = false
				this.dispatchEvent(new Event("disconnect"))
				throw new Error("Device disconnected during read")
			}

			console.error("Error reading data:", error)
			throw error
		} finally {
			this.readInProgress = false
		}
	}

	/**
	 * Write data to the device asynchronously
	 * @param buffer Data to write
	 * @returns Promise that resolves with the result of the write operation
	 */
	public async writeAsync(buffer: BufferSource): Promise<USBOutTransferResult> {
		if (!this.isConnected) {
			throw new Error("Device not connected")
		}

		try {
			// Send carriage return first (FTDI protocol requirement). This should get rid of any data that is still on the wire.
			await this.device.transferOut(
				this.outEndpoint,
				new TextEncoder().encode("\r"),
			)

			// Send the actual data
			return await this.device.transferOut(this.outEndpoint, buffer)
		} catch (error) {
			if (error instanceof DOMException && error.name === "NotFoundError") {
				this.isConnected = false
				this.dispatchEvent(new Event("disconnect"))
				throw new Error("Device disconnected during write")
			}

			console.error("Error writing data:", error)
			throw error
		}
	}

	/**
	 * Close the device connection asynchronously
	 * @returns Promise that resolves when device is closed
	 */
	public async closeAsync(): Promise<void> {
		if (this.isClosing || !this.isConnected) {
			return
		}

		this.isClosing = true

		try {
			// Stop the read loop by setting flags
			this.isConnected = false

			// Clean up device resources
			if (this.device.opened) {
				await this.device.releaseInterface(0)
				await this.device.close()
			}

			console.debug("Device closed successfully")
			this.dispatchEvent(new Event("close"))
		} catch (error) {
			console.error("Error closing device:", error)
			this.dispatchEvent(new CustomEvent("error", { detail: error }))
			throw error
		} finally {
			this.isClosing = false
		}
	}

	/**
	 * Request an FTDI device from the browser
	 * @param options Optional FTDI options
	 * @returns Promise that resolves with a new FTDI instance
	 */
	public static async requestDevice(
		options: FTDIOptions = { baudRate: 9600 },
	): Promise<FTDI> {
		try {
			const device = await navigator.usb.requestDevice({
				filters: FTDI_DEVICE_FILTERS,
			})

			console.debug("FTDI device selected:", device)
			return new FTDI(device, options)
		} catch (error) {
			console.error("Error requesting FTDI device:", error)
			throw error
		}
	}
}
